import {
  AssignmentTypeEnum,
  CaseTypeEnum,
  RouteAnnonsApproachKindEnum,
  RouteAnnonsOptionDTO,
  RouteAnnonsUnavailableReasonEnum,
  RouteConnectionDTO,
  RouteConnectionIssueEnum,
  RouteConnectionModeEnum,
  RouteDriverSwapDTO,
  RouteFieldTesterSuggestionDTO,
  RouteFillerUnavailableReasonEnum,
  RouteGapSuggestionsDTO,
  RouteHomeGroupDTO,
  RouteHubStopoverOptionDTO,
  RouteLegDTO,
  RoutePackageDTO,
  RoutePlanActionDTO,
  RoutePlanActionTypeEnum,
  RoutePlanManualReasonEnum,
  RoutePlanPlanningClassEnum,
  RoutePlanRideAlongCarrierPackageDTO,
  RoutePlanTraceEntryDTO,
  RoutePlanWarningEnum,
  RouteRideAlongOptionDTO,
} from 'api/model';
import { bestGapChoice } from './tb-remedies';
import {
  getManualReasonSeverity,
  getPersonalTransportUnavailableLabel,
} from './warning-helpers';

export type GapChoice =
  | 'personalTransport'
  | 'assignmentRequest'
  | 'filler'
  /** Ställ bilen på en Zäkra-hub och lämna resten av sträckan oplanerad (punkt 4). */
  | 'hubStopover'
  /** Åk med i en annan fälttestares bil — bilen ska ändå åt rätt håll (punkt 9). */
  | 'rideAlong'
  | 'none';

export interface PackageSelection {
  includePackage: boolean;
  /** Vald kandidat (Byt fälttestare) — default rank 1. */
  candidateUserId: number | null;
  /** Fritt vald FT utanför topp-kandidaterna, omvärderad av servern (rank 0). */
  manualCandidate?: RouteFieldTesterSuggestionDTO;
  firstGapChoice: GapChoice;
  lastGapChoice: GapChoice;
  /**
   * Val per intern överföring, nycklad på benet överföringen leder TILL (leg.assignmentId).
   * En karta och inte ett fält, eftersom ett paket kan ha flera överföringar över tröskeln.
   * Saknad nyckel = 'none', alltså samma default som första och sista benets luckor.
   */
  transferGapChoices: Record<number, GapChoice>;
  /** "Övernattning är ordnad" — krävs för att verkställa paket med HotelNeeded. */
  hotelConfirmed: boolean;
}

/**
 * Standardvalet är förslag A:s kärna: apply markerar BARA den automatiska listan. Ett manuellt
 * paket är per definition ett dödläge någon ska titta på, och att låta det följa med i en
 * massverkställan vore att verkställa just det man bad planeraren granska.
 */
/**
 * Kör den här förarens utfyllnad gruppens bil hem, med minst en passagerare? Då förväljs
 * utfyllnaden: bestGapChoice ser bara den här luckans TB, inte passagerarnas tåg som bilen sparar,
 * och valde ofta tåg eller annons — varpå passagerarnas samåkning var spärrad tills TL bytte.
 */
const isGroupCarDriver = (
  packageIndex: number,
  gap: RouteGapSuggestionsDTO | null | undefined
): boolean => {
  const group = gap?.homeGroup;
  return (
    group != null &&
    group.carAssignmentId != null &&
    group.carDriverPackageIndex === packageIndex &&
    group.carPassengerFieldTesterIds.length > 0 &&
    gap?.filler?.candidateAssignmentIds[0] === group.carAssignmentId
  );
};

export const defaultSelection = (pkg: RoutePackageDTO): PackageSelection => ({
  includePackage:
    pkg.candidates.length > 0 &&
    pkg.planningClass === RoutePlanPlanningClassEnum.Auto,
  candidateUserId: pkg.candidates[0]?.userId ?? null,
  // Förvalet är åtgärden med högst TB, inte "gör inget". Se bestGapChoice: en lucka som lämnas
  // orörd betyder att fälttestaren tar sig hem på egen hand, och det var förvalt även när ett
  // annonsben hade gett flera hundra kronor.
  firstGapChoice: bestGapChoice(pkg.candidates[0]?.firstLegGap),
  lastGapChoice: isGroupCarDriver(pkg.packageIndex, pkg.candidates[0]?.lastLegGap)
    ? 'filler'
    : bestGapChoice(pkg.candidates[0]?.lastLegGap),
  // Samma förval som luckorna: åtgärden med högst TB, inte "gör inget". En överföring som
  // lämnas orörd betyder att fälttestaren tar sig vidare på egen hand.
  transferGapChoices: Object.fromEntries(
    pkg.legs
      .filter((leg) => leg.transferGap)
      .map((leg) => [leg.assignmentId, bestGapChoice(leg.transferGap)])
  ),
  hotelConfirmed: false,
});

export const getSelectedCandidate = (
  pkg: RoutePackageDTO,
  selection: PackageSelection
): RouteFieldTesterSuggestionDTO | undefined => {
  if (
    selection.manualCandidate &&
    selection.manualCandidate.userId === selection.candidateUserId
  ) {
    return selection.manualCandidate;
  }
  return (
    pkg.candidates.find((c) => c.userId === selection.candidateUserId) ??
    pkg.candidates[0]
  );
};

/**
 * Finns bilen som samåkningen räknar med, med de val TL har gjort just nu?
 *
 * Ett vanligt bärarben körs så snart bärarens paket verkställs. En samordnad hemresa
 * (`carrierIsFiller`) åker i stället med i bärarens UTFYLLNADSBIL — den finns bara om bäraren
 * fortfarande är föraren och hennes hemresa är just den utfyllnaden. Byter TL hemresa eller förare
 * på bärarpaketet ska passageraren inte få ett samåkningsärende till en bil ingen kör.
 */
export const isRideAlongCarrierReady = (
  ride: RouteRideAlongOptionDTO,
  carrierPkg: RoutePackageDTO | undefined,
  carrierSelection: PackageSelection | undefined
): boolean => {
  if (!carrierPkg || carrierSelection?.includePackage !== true) return false;
  if (!ride.carrierIsFiller) return true;

  const carrier = getSelectedCandidate(carrierPkg, carrierSelection);
  return (
    carrier?.userId === ride.carrierFieldTesterId &&
    carrierSelection.lastGapChoice === 'filler' &&
    carrier.lastLegGap?.filler?.candidateAssignmentIds[0] ===
      ride.carrierAssignmentId
  );
};

/**
 * Utbudet för samåkning när ett paket omvärderas ("Byt fälttestare"): de andra paketens ben med
 * den förare TL har valt för dem just nu. Servern har ingen körning att söka i vid omvärderingen,
 * så utan listan står den nya förarens luckor utan samåkning.
 */
export const buildRideAlongCarriers = (
  packages: RoutePackageDTO[],
  selections: Record<number, PackageSelection>,
  excludePackageIndex: number
): RoutePlanRideAlongCarrierPackageDTO[] =>
  packages
    .filter((p) => p.packageIndex !== excludePackageIndex)
    .flatMap((p) => {
      const candidate = getSelectedCandidate(
        p,
        selections[p.packageIndex] ?? defaultSelection(p)
      );
      if (!candidate) return [];
      return [
        {
          packageIndex: p.packageIndex,
          fieldTesterId: candidate.userId,
          fieldTesterName: candidate.fullName,
          legs: p.legs,
        },
      ];
    });

/** Varningar som handlar om att FT:n inte kommer hem — de löses av en vald hemrese-åtgärd. */
const homeRemedyResolvableWarnings: RoutePlanWarningEnum[] = [
  RoutePlanWarningEnum.EndsAwayFromHome,
  RoutePlanWarningEnum.HotelNeeded,
  RoutePlanWarningEnum.HotelNeededButCannotStayAtHotels,
];

/** En vald åtgärd för sista-bens-gapet (persontransport/annons/utfyllnad) tar FT:n hemåt. */
export const hasHomeRemedy = (selection: PackageSelection): boolean =>
  selection.lastGapChoice !== 'none';

/**
 * Är hemresan löst? Antingen genom en vald lucka-åtgärd, eller genom ett skifte.
 *
 * Skiftet är den starkaste hemrese-åtgärden som finns: föraren kör motpartens fordon hem till
 * sitt EGET ankare, så varken hotell eller persontransport behövs. Utan den här kopplingen
 * behöll kortet varningarna "Slutar utanför hemområdet" och "Hotell behöver bokas" samtidigt som
 * skiftesrutan intill sa "Båda är hemma samma kväll" — och hotellspärren blockerade verkställ av
 * ett paket som inte behöver hotell.
 */
export const isHomewardSolved = (
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO
): boolean => hasHomeRemedy(selection) || !!swap;

export const isWarningResolvedByHomeRemedy = (
  warning: RoutePlanWarningEnum,
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO
): boolean =>
  isHomewardSolved(selection, swap) &&
  homeRemedyResolvableWarnings.includes(warning);

/** Hotell krävs bara när hemresan är olöst — skifte eller persontransport ersätter övernattningen. */
export const candidateNeedsHotel = (
  candidate: RouteFieldTesterSuggestionDTO | undefined,
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO
): boolean =>
  !!candidate?.warnings.includes(RoutePlanWarningEnum.HotelNeeded) &&
  !isHomewardSolved(selection, swap);

/**
 * Manuella orsaker som en markering på kortet INTE kan lösa — alltså de som måste hålla kvar
 * spärren även när planeraren kryssar i rutan.
 *
 * Gränsen går vid den allvarlighetsgrad vyn redan sätter: `blocking` är ett dödläge som kräver ett
 * annat upplägg, `decision` är något planeraren får avgöra i dagens plan. Att kryssa i ett paket
 * med "Mellanlandning på utresa" ÄR det ja åtgärden väntar på; att kryssa i ett med "Orimlig
 * utresa" är att verkställa en dag som inte går att köra.
 *
 * Undantaget är hemresan. `NoReturnTransit` sätts av backend på körningens underlag, men
 * luckväljaren löser den här i vyn — och har planeraren valt en åtgärd är orsaken inte längre
 * sann. Att låta den spärra hade gjort hela fliken oåtkomlig, eftersom det är den vanligaste
 * orsaken och just den kortet finns för att kunna svara på.
 */
const hasUnresolvedManualReason = (
  pkg: RoutePackageDTO,
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO
): boolean =>
  pkg.manualReasons.some((reason) =>
    reason === RoutePlanManualReasonEnum.NoReturnTransit
      ? !isHomewardSolved(selection, swap)
      : getManualReasonSeverity(reason) === 'blocking'
  );

/** Blockerande läge: paketet kan inte verkställas som det är. */
export const isPackageBlocked = (
  pkg: RoutePackageDTO,
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO
): boolean => {
  if (hasUnresolvedManualReason(pkg, selection, swap)) return true;
  const candidate = getSelectedCandidate(pkg, selection);
  if (!candidate) return true;
  if (!candidate.policyOk) return true;
  if (
    candidate.warnings.includes(
      RoutePlanWarningEnum.HotelNeededButCannotStayAtHotels
    ) &&
    !isHomewardSolved(selection, swap)
  ) {
    return true;
  }
  if (
    candidateNeedsHotel(candidate, selection, swap) &&
    !selection.hotelConfirmed
  ) {
    return true;
  }
  return false;
};

/**
 * Varför samåkningen på sista luckan inte går igenom, eller varför hemresegruppen inte längre
 * gäller — en mening till TL, eller undefined när allt stämmer.
 *
 * Samåkningens spärr är tyst: paketet ger noll åtgärder, och utan den här raden såg det ut som
 * att verkställ bara hoppade över det. Och "Byt fälttestare" räknar inte om gruppen (servern har
 * ingen körning att gruppera i), så de andras rutor pekade på en förare som inte längre är vald.
 */
export const describeHomeRideStatus = (
  pkg: RoutePackageDTO,
  selection: PackageSelection,
  packages: RoutePackageDTO[],
  selections: Record<number, PackageSelection>
): string | undefined => {
  const candidate = getSelectedCandidate(pkg, selection);
  const gap = candidate?.lastLegGap;
  const ride = gap?.rideAlong;

  if (selection.lastGapChoice === 'rideAlong' && ride) {
    const carrierPkg = packages.find((p) => p.packageIndex === ride.carrierPackageIndex);
    const carrierSelection = carrierPkg
      ? selections[carrierPkg.packageIndex] ?? defaultSelection(carrierPkg)
      : undefined;
    if (!isRideAlongCarrierReady(ride, carrierPkg, carrierSelection)) {
      const name = ride.carrierFieldTesterName;
      if (!carrierPkg || !carrierSelection?.includePackage) {
        return `Samåkningen verkställs inte: ${name}s paket är inte markerat för verkställ.`;
      }
      const driver = getSelectedCandidate(carrierPkg, carrierSelection);
      if (driver?.userId !== ride.carrierFieldTesterId) {
        return `Samåkningen verkställs inte: ${name} kör inte längre bilen — ${driver?.fullName ?? 'någon annan'} är vald på paketet.`;
      }
      return `Samåkningen verkställs inte: välj "Bil åt rätt håll" som hemresa på ${name}s paket — det är den bilen som tar hem gruppen.`;
    }
  }

  const group = gap?.homeGroup;
  if (!group) return undefined;
  // Med bil spelar bara föraren och passagerarna roll — den som bor åt ett annat håll åkte aldrig
  // med, och ett byte på hennes paket ändrar ingenting för bilen.
  const inCar = (m: { packageIndex: number; fieldTesterId: number }) =>
    group.carAssignmentId == null ||
    m.packageIndex === group.carDriverPackageIndex ||
    group.carPassengerFieldTesterIds.includes(m.fieldTesterId);
  const switched = group.members.filter(inCar).filter((m) => {
    const p = packages.find((x) => x.packageIndex === m.packageIndex);
    if (!p) return false;
    const current = getSelectedCandidate(p, selections[p.packageIndex] ?? defaultSelection(p));
    return current?.userId !== m.fieldTesterId;
  });
  if (switched.length === 0) return undefined;
  const names = switched.map((m) => m.fieldTesterName).join(', ');
  return group.carAssignmentId == null
    ? `Gruppen gäller inte längre som den visas: ${names} har bytts på sitt paket.`
    : `Samåkningen i bilen stämmer inte längre: ${names} har bytts på sitt paket. Kör om planeringen för en ny grupp.`;
};

/**
 * När gruppens utfyllnadsbil `carAssignmentId` går, med de val TL har gjort just nu — eller
 * undefined om ingen har valt att åka med.
 *
 * Avgången i förslaget väntar in ALLA tänkta passagerare. Den som valt tåget ska inte vänta in,
 * så tiden räknas om på föraren och de passagerare som faktiskt valt samåkningen: den senaste
 * `readyAtCar` (klar plus vägen till bilen). Både förarens utfyllnad och passagerarnas samåkning
 * verkställs på den tiden, så bilen och samåkningen står på samma klockslag.
 */
export const groupCarDeparture = (
  packages: RoutePackageDTO[],
  selections: Record<number, PackageSelection>,
  carAssignmentId: number | null | undefined
): Date | undefined => {
  if (carAssignmentId == null) return undefined;

  const riding = packages.filter((p) => {
    const selection = selections[p.packageIndex];
    // Ett spärrat paket verkställs inte — då kommer hon inte heller till bilen.
    if (
      !selection?.includePackage ||
      selection.lastGapChoice !== 'rideAlong' ||
      isPackageBlocked(p, selection)
    ) {
      return false;
    }
    const ride = getSelectedCandidate(p, selection)?.lastLegGap?.rideAlong;
    return ride?.carrierIsFiller === true && ride.carrierAssignmentId === carAssignmentId;
  });
  if (riding.length === 0) return undefined;

  const group = getSelectedCandidate(riding[0], selections[riding[0].packageIndex])
    ?.lastLegGap?.homeGroup;
  if (!group) return undefined;

  const counted = new Set([
    group.carDriverPackageIndex,
    ...riding.map((p) => p.packageIndex),
  ]);
  const times = group.members
    .filter((m) => counted.has(m.packageIndex))
    .map((m) => new Date(m.readyAtCar ?? m.finishedAt).getTime());
  return times.length > 0 ? new Date(Math.max(...times)) : new Date(group.departureTime);
};

/**
 * Persontransport för kedjans INTERNA överföringar — mellan ben N och ben N+1.
 *
 * Till skillnad från första och sista benets luckor är det här inget VAL. Fälttestaren har just
 * lämnat ifrån sig fordonet och måste ta sig till nästa upphämtning; frågan är bara om resan
 * bokförs eller sker i det tysta. Därför genereras åtgärden automatiskt för varje ben som backend
 * flaggat med `transferNeedsPersonalTransport`, utan väljare.
 *
 * Uppdraget fästs på det ben det föregår — samma modell som första benets lucka redan använder.
 */
/**
 * Persontransport för de överföringar som INTE har en luckväljare.
 *
 * Har benet ett `transferGap` äger väljaren överföringen, och åtgärden skapas av
 * `buildGapActions` enligt trafikledarens val. Utan det undantaget skapades TVÅ
 * persontransportuppdrag för samma överföring — ett här och ett från väljaren, som dessutom
 * förvaljer persontransport när annons och utfyllnad saknas. Och "gör inget" i väljaren måste
 * betyda just det.
 *
 * Undantaget är en ren håltimme (`isIdleWaitOnly`): väljaren erbjuder bara utfyllnad, och
 * överföringen ägs fortfarande av den här hjälparen — utom när utfyllnaden väljs, för då åker
 * fälttestaren via utfyllnaden och inte direkt till nästa ben.
 */
const buildInternalTransferActions = (
  pkg: RoutePackageDTO,
  selection: PackageSelection,
  fieldTesterId: number
): RoutePlanActionDTO[] =>
  pkg.legs
    .filter(
      (leg) =>
        (!leg.transferGap ||
          (leg.transferGap.isIdleWaitOnly &&
            (selection.transferGapChoices?.[leg.assignmentId] ?? 'none') !== 'filler')) &&
        leg.transferNeedsPersonalTransport &&
        leg.transferTransit
    )
    .map((leg) => ({
      actionType: RoutePlanActionTypeEnum.CreatePersonalTransport,
      caseId: leg.caseId,
      fieldTesterId,
      personalTransportType: AssignmentTypeEnum.GoByPublicTransport,
      startTime: new Date(leg.transferTransit!.departureTimeEpoch * 1000),
      endTime: new Date(leg.transferTransit!.arrivalTimeEpoch * 1000),
      setHotelStayover: false,
    }));

/**
 * Tillresan till logistikcentret när annonsen väljs (betygsfeedback paket #49): en annonsbil
 * går alltid logistikcenter → anläggning, så står fälttestaren inte där måste resan dit
 * verkställas tillsammans med förfrågan — som utfyllnadens tilldelning eller som
 * persontransport. Null när hon redan står vid logistikcentret.
 */
const buildAnnonsApproachAction = (
  annons: RouteAnnonsOptionDTO,
  fieldTesterId: number,
  adjacentLeg: { caseId: number; assignmentId: number } | undefined
): RoutePlanActionDTO | null => {
  if (
    annons.approachKind === RouteAnnonsApproachKindEnum.Filler &&
    annons.approachFiller?.topCandidateAssignmentId != null
  ) {
    return {
      actionType: RoutePlanActionTypeEnum.AssignFieldTester,
      assignmentId: annons.approachFiller.topCandidateAssignmentId,
      fieldTesterId,
      plannedStart: annons.approachFillerPlannedStart,
      setHotelStayover: false,
    };
  }

  if (
    annons.approachKind === RouteAnnonsApproachKindEnum.PersonalTransport &&
    annons.approachTransit
  ) {
    const t = annons.approachTransit;
    return {
      actionType: RoutePlanActionTypeEnum.CreatePersonalTransport,
      caseId: adjacentLeg?.caseId,
      fieldTesterId,
      personalTransportType: AssignmentTypeEnum.GoByTrain,
      startTime: new Date(t.departureTimeEpoch * 1000),
      endTime: new Date(t.arrivalTimeEpoch * 1000),
      setHotelStayover: false,
    };
  }

  return null;
};

/**
 * Gör om relativa beroenden (`requiresActionIndex` < 0, "så många rader bakåt") till index i
 * listan. Beroendena uppstår i buildGapActions, som inte vet var i paketet åtgärderna hamnar.
 */
const resolveRelativeDependencies = (
  actions: RoutePlanActionDTO[]
): RoutePlanActionDTO[] =>
  actions.map((action, index) =>
    action.requiresActionIndex != null && action.requiresActionIndex < 0
      ? { ...action, requiresActionIndex: index + action.requiresActionIndex }
      : action
  );

/**
 * Lägger ihop paketens åtgärder till EN begäran. Beroendena (`requiresActionIndex`) pekar inom
 * sitt paket och flyttas med paketets startposition — servern läser dem som index i hela
 * begäran.
 */
export const flattenPlanActions = (
  plans: PackageActionPlan[]
): RoutePlanActionDTO[] => {
  let offset = 0;
  return plans.flatMap((plan) => {
    const start = offset;
    offset += plan.actions.length;
    return plan.actions.map((action) =>
      action.requiresActionIndex != null
        ? { ...action, requiresActionIndex: action.requiresActionIndex + start }
        : action
    );
  });
};

/** Persontransportuppdraget för samåkningens anslutning med kollektivtrafik; inget för en promenad. */
const buildConnectionActions = (
  connection: RouteConnectionDTO | undefined,
  fieldTesterId: number,
  adjacentLeg: { caseId: number } | undefined
): RoutePlanActionDTO[] =>
  connection?.mode === RouteConnectionModeEnum.Transit && connection.transit
    ? [
        {
          actionType: RoutePlanActionTypeEnum.CreatePersonalTransport,
          caseId: adjacentLeg?.caseId,
          fieldTesterId,
          personalTransportType: AssignmentTypeEnum.GoByTrain,
          startTime: new Date(connection.transit.departureTimeEpoch * 1000),
          endTime: new Date(connection.transit.arrivalTimeEpoch * 1000),
          setHotelStayover: false,
        },
      ]
    : [];

const buildGapActions = (
  gap: RouteGapSuggestionsDTO | null | undefined,
  choice: GapChoice,
  fieldTesterId: number,
  adjacentLeg: { caseId: number; assignmentId: number } | undefined,
  carDeparture?: Date
): RoutePlanActionDTO[] => {
  if (!gap || choice === 'none') return [];

  if (choice === 'personalTransport' && gap.personalTransport) {
    const t = gap.personalTransport;
    return [
      {
        actionType: RoutePlanActionTypeEnum.CreatePersonalTransport,
        caseId: adjacentLeg?.caseId,
        fieldTesterId,
        personalTransportType: AssignmentTypeEnum.GoByTrain,
        startTime: new Date(t.departureTimeEpoch * 1000),
        endTime: new Date(t.arrivalTimeEpoch * 1000),
        setHotelStayover: false,
      },
    ];
  }

  if (choice === 'assignmentRequest' && gap.assignmentRequest) {
    const a = gap.assignmentRequest;
    const approach = buildAnnonsApproachAction(a, fieldTesterId, adjacentLeg);
    const request: RoutePlanActionDTO = {
      actionType: RoutePlanActionTypeEnum.CreateAssignmentRequest,
      fieldTesterId,
      fromCompanyId: a.fromCompanyId,
      toCompanyId: a.toCompanyId,
      targetStartTime: a.targetStartTime,
      targetEndTime: a.targetEndTime,
      setHotelStayover: false,
      // Förfrågan beror på tillresan: faller den skapas ingen annonsbil i Strängnäs åt någon
      // som inte kan ta sig dit. Relativt index (−1 = raden före) — buildPackageActions gör om
      // det till paketets index, och flattenPlanActions till hela begärans.
      requiresActionIndex: approach ? -1 : undefined,
    };
    return approach ? [approach, request] : [request];
  }

  if (choice === 'rideAlong' && gap.rideAlong) {
    // Skapar ett eget Samåknings-ärende för passageraren, länkat till bärarens uppdrag.
    // Ingen biljettkostnad: hon åker med i en bil som ändå körs.
    const ride = gap.rideAlong;
    // Gruppens bil går när de som faktiskt åker med är vid den — samma tid som förarens
    // utfyllnad verkställs på. Restiden är bilens, oförändrad.
    const start = ride.carrierIsFiller && carDeparture ? carDeparture : ride.departureTime;
    const end = new Date(
      new Date(start).getTime() +
        (new Date(ride.arrivalTime).getTime() - new Date(ride.departureTime).getTime())
    );
    return [
      {
        actionType: RoutePlanActionTypeEnum.CreateRideAlong,
        carrierAssignmentId: ride.carrierAssignmentId,
        // Bara för utfyllnadsbilen: där är föraren hela poängen, och servern fäller raden om
        // någon annan fått bilen. Ett vanligt bärarben kör samma sträcka vem som än kör det —
        // efter "Byt fälttestare" eller med rank 2 ska samåkningen fortfarande gå igenom.
        expectedCarrierFieldTesterId: ride.carrierIsFiller
          ? ride.carrierFieldTesterId
          : undefined,
        fieldTesterId,
        startTime: start,
        endTime: end,
        setHotelStayover: false,
      },
      // Tåget eller bussen till bilen (hem) eller från avstigningen (ut), när det inte går att gå.
      ...buildConnectionActions(ride.connection, fieldTesterId, adjacentLeg),
    ];
  }

  if (choice === 'hubStopover' && gap.hubStopover) {
    // Delar sista benet vid hubben. Andra halvan lämnas OPLANERAD av backend — vem som kör den
    // vet man inte i förväg, och bilen blir en mellanlandad bil för nästa dags planering.
    return [
      {
        actionType: RoutePlanActionTypeEnum.CreatePlannedStopover,
        assignmentId: adjacentLeg?.assignmentId,
        fieldTesterId,
        hubCompanyId: gap.hubStopover.hubCompanyId,
        setHotelStayover: false,
      },
    ];
  }

  if (
    choice === 'filler' &&
    gap.filler &&
    gap.filler.candidateAssignmentIds.length > 0
  ) {
    const assignmentId = gap.filler.candidateAssignmentIds[0];
    // Är utfyllnaden gruppens bil hem, och åker någon faktiskt med, går den när föraren och de
    // passagerarna är vid bilen — samma tid som samåkningen bokas på. Annars räknar servern ut
    // en egen start: utan passagerare finns ingen att vänta in.
    const groupStart =
      gap.homeGroup?.carAssignmentId === assignmentId ? carDeparture : undefined;
    return [
      {
        actionType: RoutePlanActionTypeEnum.AssignFieldTester,
        assignmentId,
        fieldTesterId,
        plannedStart: groupStart,
        setHotelStayover: false,
      },
    ];
  }

  return [];
};

export interface PackageActionPlan {
  packageIndex: number;
  actions: RoutePlanActionDTO[];
}

/**
 * Skiftestransport verkställs som EN atomär action för hela paret: servern delar båda ärendena
 * vid bytespunkten och tilldelar de fyra benen korsvis. Den läggs därför bara på parets ena
 * sida (sida A) — annars skulle bytet utföras två gånger.
 */
export const buildDriverSwapAction = (
  swap: RouteDriverSwapDTO
): RoutePlanActionDTO => ({
  actionType: RoutePlanActionTypeEnum.ApplyDriverSwap,
  // Övernattning är själva poängen med bytet att slippa — därför alltid false här.
  setHotelStayover: false,
  swapPointCompanyId: swap.pointCompanyId,
  swapMeetingTime: swap.meetingTime,
  swapAssignmentIdA: swap.sideA.assignmentId,
  swapFieldTesterIdA: swap.sideA.fieldTesterId ?? undefined,
  swapAssignmentIdB: swap.sideB.assignmentId,
  swapFieldTesterIdB: swap.sideB.fieldTesterId ?? undefined,
});

/** Bygger apply-actions för ett paket utifrån valet. Tomt = paketet kan/ska inte verkställas. */
/** Motparten i ett skifte, när den behövs för att avgöra om paret går att verkställa. */
export interface SwapPartner {
  pkg: RoutePackageDTO;
  selection: PackageSelection;
}

export const buildPackageActions = (
  pkg: RoutePackageDTO,
  selection: PackageSelection,
  swap?: RouteDriverSwapDTO,
  partner?: SwapPartner,
  /**
   * Är bärarens paket markerat för verkställ? Samåkningen är värdelös om bilen aldrig körs, så
   * valet får inte gå igenom halvt — samma disciplin som skiftets parkontroll.
   * `undefined` = ingen samåkning vald, alltså inget att kontrollera.
   */
  rideAlongCarrierSelected?: boolean,
  /**
   * När gruppens utfyllnadsbil går (groupCarDeparture) — för föraren och för passagerarna i den.
   * `undefined` = ingen åker med, och då planeras utfyllnaden på sin egen tid.
   */
  groupCarDepartureTime?: Date
): RoutePlanActionDTO[] => {
  const candidate = getSelectedCandidate(pkg, selection);
  if (!selection.includePackage || !candidate) return [];
  if (isPackageBlocked(pkg, selection, swap)) return [];

  if (swap) {
    // Bytet ersätter både tilldelningen och lucka-åtgärderna: förarna kör hem varandras
    // fordon, så det finns ingen lucka kvar att lösa. Sida B bidrar med noll actions
    // eftersom sida A:s action redan tilldelar alla fyra benen.
    if (pkg.packageIndex !== swap.sideA.packageIndex) return [];

    // BÅDA sidorna måste gå att verkställa. Tidigare prövades bara sida A, så en
    // körtidsblockerad motpart stoppade inte batchen: skiftet skickades och avvisades först av
    // ApplyDriverSwapAsync. Inget skadades — allt ligger i ett SaveChanges — men användaren fick
    // ett misslyckat verkställ i stället för en avstängd knapp.
    if (
      partner &&
      (!partner.selection.includePackage ||
        isPackageBlocked(partner.pkg, partner.selection, swap))
    ) {
      return [];
    }

    return [buildDriverSwapAction(swap)];
  }

  // Samåkningen hänger på att bärarens paket faktiskt verkställs. Lämnas det omarkerat skulle
  // passageraren få ett samåkningsärende till en bil ingen kör.
  const ridesAlong =
    (selection.firstGapChoice === 'rideAlong' &&
      candidate.firstLegGap?.rideAlong) ||
    (selection.lastGapChoice === 'rideAlong' &&
      candidate.lastLegGap?.rideAlong);
  if (ridesAlong && rideAlongCarrierSelected === false) {
    return [];
  }

  const needsHotelStayover =
    candidateNeedsHotel(candidate, selection, swap) && selection.hotelConfirmed;
  const lastLegId = pkg.legs[pkg.legs.length - 1]?.assignmentId;

  // Mellanlandningen DELAR sista benet och tilldelar första halvan själv
  // (ApplyCreatePlannedStopoverAsync → ApplySwapOutboundLeg). Skickas ett AssignFieldTester för
  // samma uppdrag före den, hinner det sätta AssignedToID och mellanlandningen faller på
  // "Uppdraget är redan planerat" — åtgärden gick alltså aldrig att verkställa.
  const stopoverLegId =
    selection.lastGapChoice === 'hubStopover' &&
    candidate.lastLegGap?.hubStopover
      ? lastLegId
      : undefined;

  const legActions: RoutePlanActionDTO[] = pkg.legs
    .filter((leg) => leg.assignmentId !== stopoverLegId)
    .map((leg): RoutePlanActionDTO => {
      // Mellanlandning mitt i paketet (TL-feedback 2026-10-13): paketet är räknat på att benet
      // slutar vid platsen och att nästa ben börjar därifrån. Benet delas därför alltid — samma
      // åtgärd som en vald mellanlandning på sista benet, men med planerad start, eftersom det
      // inte är sista benet och tiden inte går att räkna bakåt från bokad tid.
      if (leg.stopoverCompanyId != null) {
        return {
          actionType: RoutePlanActionTypeEnum.CreatePlannedStopover,
          assignmentId: leg.assignmentId,
          fieldTesterId: candidate.userId,
          hubCompanyId: leg.stopoverCompanyId,
          plannedStart: leg.plannedStart,
          setHotelStayover: false,
        };
      }

      return {
        actionType: RoutePlanActionTypeEnum.AssignFieldTester,
        assignmentId: leg.assignmentId,
        fieldTesterId: candidate.userId,
        plannedStart: leg.plannedStart,
        // Tiden paketet räknades på. Servern fäller raden om en annan tid står i databasen —
        // t.ex. ett ombokningsförslag som kunden inte bekräftat än.
        expectedBookedTo: leg.proposedBookedTo ?? leg.bookedTo,
        setHotelStayover: needsHotelStayover && leg.assignmentId === lastLegId,
        // Planerarens rastförslag verkställs tillsammans med tilldelningen.
        breakTimeMinutes:
          candidate.breakSuggestion?.assignmentId === leg.assignmentId
            ? candidate.breakSuggestion.breakMinutes
            : undefined,
      };
    });

  return resolveRelativeDependencies([
    ...legActions,
    ...buildInternalTransferActions(pkg, selection, candidate.userId),
    ...buildGapActions(
      candidate.firstLegGap,
      selection.firstGapChoice,
      candidate.userId,
      pkg.legs[0]
    ),
    ...buildGapActions(
      candidate.lastLegGap,
      selection.lastGapChoice,
      candidate.userId,
      pkg.legs[pkg.legs.length - 1],
      groupCarDepartureTime
    ),
    // Överföringarnas val. Samma hjälpare som luckorna — problemet är detsamma, och åtgärderna
    // (persontransport, annonsförfrågan, utfyllnad) skapas på exakt samma sätt. Benet som
    // skickas in är det överföringen leder TILL, alltså det fälttestaren ska ta sig till.
    ...pkg.legs.flatMap((leg) =>
      leg.transferGap
        ? buildGapActions(
            leg.transferGap,
            selection.transferGapChoices?.[leg.assignmentId] ?? 'none',
            candidate.userId,
            leg
          )
        : []
    ),
  ]);
};

export interface ApplySummary {
  packageCount: number;
  assignmentCount: number;
  personalTransportCount: number;
  assignmentRequestCount: number;
  hotelStayoverCount: number;
  driverSwapCount: number;
}

/**
 * Ett skifte skriver FYRA uppdrag: ApplyDriverSwapAsync muterar båda originalen till utgående ben
 * och skapar två returben, vart och ett med sin egen förare.
 *
 * Måste räknas explicit, för skiftet är EN action medan de andra är en per uppdrag. Utan detta sa
 * bekräftelsedialogen "0 uppdrag sätts till Planerad" om ett skifte som skriver fyra rader — den
 * mest ingripande åtgärden i hela funktionen såg ut att inte göra någonting.
 */
const AssignmentsWrittenPerDriverSwap = 4;

export const summarizeActions = (plans: PackageActionPlan[]): ApplySummary => {
  const all = plans.flatMap((p) => p.actions);
  const driverSwapCount = all.filter(
    (a) => a.actionType === RoutePlanActionTypeEnum.ApplyDriverSwap
  ).length;
  return {
    packageCount: plans.filter((p) => p.actions.length > 0).length,
    driverSwapCount,
    assignmentCount:
      all.filter(
        (a) => a.actionType === RoutePlanActionTypeEnum.AssignFieldTester
      ).length +
      driverSwapCount * AssignmentsWrittenPerDriverSwap,
    personalTransportCount: all.filter(
      (a) => a.actionType === RoutePlanActionTypeEnum.CreatePersonalTransport
    ).length,
    assignmentRequestCount: all.filter(
      (a) => a.actionType === RoutePlanActionTypeEnum.CreateAssignmentRequest
    ).length,
    hotelStayoverCount: all.filter((a) => a.setHotelStayover).length,
  };
};

/**
 * Live-räkning av hur många annonsförfrågningar per destination som är VALDA i UI:t just nu
 * (över alla paket). Visas på varje annonsrad som "N valda på andra ruttpaket" (minus raden
 * själv) och uppdateras direkt när man ändrar sig — det är valen som reserverar budget,
 * inte förslagen.
 */
export const countSelectedAnnonsByDestination = (
  packages: RoutePackageDTO[],
  selections: Record<number, PackageSelection>
): Record<number, number> => {
  const counts: Record<number, number> = {};
  packages.forEach((pkg) => {
    const selection = selections[pkg.packageIndex];
    if (!selection) return;
    const candidate = getSelectedCandidate(pkg, selection);
    if (!candidate) return;

    const add = (companyId: number | undefined) => {
      if (companyId == null) return;
      counts[companyId] = (counts[companyId] ?? 0) + 1;
    };

    if (selection.firstGapChoice === 'assignmentRequest') {
      add(candidate.firstLegGap?.assignmentRequest?.toCompanyId);
    }
    if (selection.lastGapChoice === 'assignmentRequest') {
      add(candidate.lastLegGap?.assignmentRequest?.toCompanyId);
    }
  });
  return counts;
};

// ── Formatering ────────────────────────────────────────────────────────────────

export const formatDurationMs = (ms: number): string => {
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${minutes.toString().padStart(2, '0')} min`;
};

/**
 * Paketets arbetstid: första upphämtningens start till sista avlämningens slut. Skiljer sig från
 * `totalOccupiedMs` (uppdragstiden, summan av uppdragens estimat) med överföringar, väntan och rast
 * däremellan. Pendling ut och hemresa ligger utanför — de har egna rader och lucka-väljare.
 */
export const packageWorkSpanMs = (pkg: {
  windowStart: Date | string;
  windowEnd: Date | string;
}): number =>
  Math.max(
    0,
    new Date(pkg.windowEnd).getTime() - new Date(pkg.windowStart).getTime()
  );

export const formatClock = (value: Date): string =>
  new Date(value).toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Den valda mellanlandningen vid hubb på sista benet, eller null. Vid ett skifte kör föraren
 * motpartens fordon hem och sista luckan finns inte (buildPackageActions skickar då bara skiftet).
 */
export const selectedHubStopover = (
  candidate: Pick<RouteFieldTesterSuggestionDTO, 'lastLegGap'> | undefined,
  selection: Pick<PackageSelection, 'lastGapChoice'>,
  swap?: RouteDriverSwapDTO
): RouteHubStopoverOptionDTO | null =>
  !swap && selection.lastGapChoice === 'hubStopover'
    ? candidate?.lastLegGap?.hubStopover ?? null
    : null;

/**
 * Målet på sista benet när mellanlandningen är vald (Feedbackknapp-9, -13, -15: benet stod kvar
 * som "Linköping → Strängnäs" fast Linköpingshubben var förvald). Samma uppgifter som
 * mellanlandningen mitt på dagen (leg.stopoverName) visar.
 */
export const describeHubStopoverDestination = (
  hub: Pick<RouteHubStopoverOptionDTO, 'hubName' | 'kmRemaining'>
): string =>
  `${hub.hubName} (mellanlandning, ${Math.round(
    hub.kmRemaining
  )} km kvar oplanerat)`;

/** Hemresan från hubben: hubbförslagets hemkomst minus framkomsten dit. */
export const hubHomewardMs = (
  hub: Pick<RouteHubStopoverOptionDTO, 'arrivalAtHub' | 'arrivalHomeWithStopover'>
): number =>
  Math.max(
    0,
    new Date(hub.arrivalHomeWithStopover).getTime() -
      new Date(hub.arrivalAtHub).getTime()
  );

/**
 * Tiden på sista benet som inte körs i dag när det slutar vid hubben: från framkomsten dit till
 * benets planerade slut. Dras av från arbetstid och uppdragstid, så att summeringen stämmer med
 * benraden. 0 utan vald mellanlandning.
 */
export const hubStopoverCutMs = (
  pkg: Pick<RoutePackageDTO, 'legs'>,
  hub: Pick<RouteHubStopoverOptionDTO, 'arrivalAtHub'> | null | undefined
): number => {
  const last = pkg.legs[pkg.legs.length - 1];
  if (!hub || !last) return 0;
  return Math.max(
    0,
    new Date(last.plannedEnd).getTime() - new Date(hub.arrivalAtHub).getTime()
  );
};

export interface WholeDay {
  start: Date;
  end: Date;
  hours: number;
  existingAssignments: number;
}

/** Hemkomsterna i hubbförslaget kan skilja en minut från dagens slut på avrundning. */
const WHOLE_DAY_END_TOLERANCE_MS = 60_000;

/**
 * Förarens dag från avresa till hemkomst, med ut- och hemresa (Feedbackknapp-2, -3: kortet sa
 * "Arbetstid 5 h" om en dag på 10,2 h med 284 min hem). Backend räknar den (EstimateDay) med
 * hemresan från sista avlämningen. Är mellanlandningen vald slutar dagen i stället vid
 * hubbförslagets hemkomst, utom när ett befintligt uppdrag ligger senare och avgör slutet.
 * Null när backend saknar pendling och hemresa.
 */
export const wholeDayFor = (
  candidate: Pick<
    RouteFieldTesterSuggestionDTO,
    | 'estimatedDayStart'
    | 'estimatedDayEnd'
    | 'estimatedDayHours'
    | 'existingAssignmentsInDay'
  >,
  hub?: Pick<
    RouteHubStopoverOptionDTO,
    'arrivalHomeWithStopover' | 'arrivalHomeWithoutStopover'
  > | null
): WholeDay | null => {
  const {
    estimatedDayStart,
    estimatedDayEnd,
    estimatedDayHours,
    existingAssignmentsInDay,
  } = candidate;
  if (!estimatedDayStart || !estimatedDayEnd || estimatedDayHours == null) {
    return null;
  }

  const start = new Date(estimatedDayStart);
  const backendEnd = new Date(estimatedDayEnd);
  // Utan befintliga uppdrag är slutet alltid paketets. Med dem avgör jämförelsen om ett senare
  // uppdrag sätter slutet.
  const endsWithPackage =
    !!hub &&
    (!existingAssignmentsInDay ||
      backendEnd.getTime() <=
        new Date(hub.arrivalHomeWithoutStopover).getTime() +
          WHOLE_DAY_END_TOLERANCE_MS);
  if (!hub || !endsWithPackage) {
    return {
      start,
      end: backendEnd,
      hours: estimatedDayHours,
      existingAssignments: existingAssignmentsInDay,
    };
  }

  const end = new Date(hub.arrivalHomeWithStopover);
  return {
    start,
    end,
    hours: Math.round((end.getTime() - start.getTime()) / 360_000) / 10,
    existingAssignments: existingAssignmentsInDay,
  };
};

/** "07:01–17:13 (10,2 h)" */
export const formatWholeDaySpan = (day: WholeDay): string =>
  `${formatClock(day.start)}–${formatClock(day.end)} (${day.hours
    .toFixed(1)
    .replace('.', ',')} h)`;

/**
 * Förarens hela dag när paketet läggs på en dag hon redan har uppdrag i (ruttprovet 2026-10-26,
 * paket #15: dagen visades som 4,3 h fast den blev 07:06–19:12). Null när paketet är hela dagen;
 * då står dagen bara på raden "Hela dagen".
 */
export const describeWholeDay = (
  candidate: Parameters<typeof wholeDayFor>[0],
  hub?: Parameters<typeof wholeDayFor>[1]
): string | null => {
  const day = wholeDayFor(candidate, hub);
  if (!day || !day.existingAssignments) {
    return null;
  }

  return `Hela dagen ${formatWholeDaySpan(day)}, med ${
    day.existingAssignments
  } uppdrag föraren redan har`;
};

/**
 * Paketet slutar mot ett uppdrag föraren redan har senare samma dag, inte hemma (2026-10-28).
 * Marginalen är tiden mellan ankomsten dit och uppdragets start; negativ = hon hinner inte.
 * Null när paketet slutar hemma.
 */
export const describeEndsBefore = (
  candidate: Pick<
    RouteFieldTesterSuggestionDTO,
    | 'endsBeforeAssignmentId'
    | 'endsBeforeStart'
    | 'endsBeforeCity'
    | 'endsBeforeSlackMinutes'
  >
): { text: string; late: boolean } | null => {
  const {
    endsBeforeAssignmentId,
    endsBeforeStart,
    endsBeforeCity,
    endsBeforeSlackMinutes,
  } = candidate;
  if (!endsBeforeAssignmentId || !endsBeforeStart) {
    return null;
  }

  const where = `${endsBeforeCity ?? 'nästa uppdrag'} ${formatClock(
    endsBeforeStart
  )}`;
  if (endsBeforeSlackMinutes != null && endsBeforeSlackMinutes < 0) {
    return {
      text: `Hinner inte till sitt befintliga uppdrag i ${where}: ${-endsBeforeSlackMinutes} min för sent`,
      late: true,
    };
  }

  const slack =
    endsBeforeSlackMinutes != null
      ? ` (${endsBeforeSlackMinutes} min marginal)`
      : '';
  return {
    text: `Slutar före sitt befintliga uppdrag i ${where}${slack}`,
    late: false,
  };
};

/**
 * Paketet har rast nog, men hela dagen med förarens befintliga uppdrag har det inte (2026-10-28).
 * Rasten som saknas läggs av rastplaneraren när paketet verkställs. Null när dagen räcker.
 */
export const describeDayBreakShortfall = (
  candidate: Pick<RouteFieldTesterSuggestionDTO, 'dayBreakShortfallMinutes'>
): string | null =>
  candidate.dayBreakShortfallMinutes
    ? `Rasten räcker för paketet men inte för hela dagen: ${candidate.dayBreakShortfallMinutes} min läggs när paketet verkställs`
    : null;

/**
 * Vilka som slutar här samtidigt och åt samma håll, och om någon av dem har en bil.
 *
 * Mätningen 2026-10-20: 1–3 sådana grupper om dagen, nästan alla vid Logistik Strängnäs mot
 * Stockholm, och var och en tog tåget hem. Utan bil är raden ändå värd att visa — TL kan ringa
 * runt och samordna, och det var just den informationen som saknades.
 */
export const describeHomeGroup = (group: RouteHomeGroupDTO): string => {
  const who = group.members
    .map((m) => `${m.fieldTesterName} ${formatClock(m.finishedAt)}`)
    .join(', ');
  const base = `${group.members.length} slutar i ${group.dropoffCity} och ska åt samma håll: ${who}.`;
  if (group.carAssignmentId == null) {
    return `${base} Ingen av dem har en bil åt rätt håll — samordna gärna själv.`;
  }
  const passengers = group.members
    .filter((m) => group.carPassengerFieldTesterIds.includes(m.fieldTesterId))
    .map((m) => m.fieldTesterName)
    .join(', ');
  const saving =
    group.estimatedSavingSek != null && group.estimatedSavingSek > 0
      ? ` Sparar ca ${group.estimatedSavingSek.toLocaleString('sv-SE')} kr mot kollektivtrafik.`
      : '';
  const kind = group.carIsStopover ? 'mellanlandad bil' : 'oplanerat uppdrag';
  const car = group.carRegistrationNumber
    ? `${group.carRegistrationNumber} (${kind})`
    : `en bil (${kind})`;
  return `${base} ${group.carDriverName} kan ta ${car} mot ${group.carToCity ?? 'hemorten'} ${formatClock(group.departureTime)} och ta med ${passengers}.${saving}`;
};

/**
 * Säger att utresan avgår ett TIDIGARE DYGN — alltså att fälttestaren måste resa kvällen innan.
 *
 * Klockslaget ensamt döljer det: "20:45 → 05:23" ser ut som en natt i samma dygn. Det verkliga
 * fallet (2026-09-15, paket 9) var en resa på 535 minuter som avgick 18:45 dagen före, och
 * kortet skrev ändå ett spann på 6,9 timmar.
 *
 * `effectiveWindowStart` kommer från backend och är satt först när avgången ligger före paketets
 * fönster — klienten gissar alltså aldrig själv. Returnerar null när resan ryms i dagen.
 */
export const describeEarlierDayDeparture = (
  effectiveWindowStart: Date | undefined,
  windowStart: Date | undefined
): string | null => {
  if (!effectiveWindowStart || !windowStart) return null;

  const departure = new Date(effectiveWindowStart);
  const reference = new Date(windowStart);
  const sameDay =
    departure.getFullYear() === reference.getFullYear() &&
    departure.getMonth() === reference.getMonth() &&
    departure.getDate() === reference.getDate();
  if (sameDay) return null;

  return `avgår ${departure.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
  })} — dagen innan`;
};

export interface PendingAcquisitionNote {
  text: string;
  /** Någon annan än den valda fälttestaren köper bilen — det är den risken TL ska ta ställning till. */
  risky: boolean;
}

/**
 * Raden "bilen är inte köpt än" på ett ben (TL-feedback 2026-09-30, paket #4). Utan den såg
 * KGM63Z ut som vilken hämtning som helst, och beroendet syntes bara i spåret.
 *
 * Jämför mot den VALDA fälttestaren, inte bara rank 1: byter trafikledaren till den som köper
 * bilen är risken borta, och byter hon bort köparen uppstår den. Ligger inköpet i samma paket
 * syns det redan som benet före — då behövs ingen rad. Returnerar null när bilen är köpt.
 */
export const describePendingAcquisition = (
  leg: RouteLegDTO,
  packageLegs: RouteLegDTO[],
  selectedCandidateUserId: number | null
): PendingAcquisitionNote | null => {
  const pending = leg.pendingAcquisition;
  if (!pending) return null;
  if (packageLegs.some((l) => l.assignmentId === pending.assignmentId))
    return null;

  const noun = leg.caseTypeID === CaseTypeEnum.TradeIn ? 'inbytet' : 'inköpet';
  const sameBuyer =
    pending.assignedToId != null &&
    pending.assignedToId === selectedCandidateUserId;
  const buyer = sameBuyer
    ? 'samma fälttestare'
    : (pending.assignedToName ??
      (pending.assignedToId != null
        ? `fälttestare ${pending.assignedToId}`
        : 'ingen än'));
  const where = pending.inProposal ? ', i ett annat paket' : '';
  const when = pending.expectedEnd
    ? `, klart ca ${formatClock(pending.expectedEnd)}`
    : '';

  return {
    text: `Bilen är inte köpt än — ${noun} (uppdrag ${pending.assignmentId}) körs av ${buyer}${where}${when}`,
    risky: !sameBuyer,
  };
};

/** Budgetsiffrorna är decimal i API:t men heltal i praktiken — visa decimal bara när den finns. */
const formatSlotCount = (value: number): string =>
  Number.isInteger(value)
    ? String(value)
    : value.toLocaleString('sv-SE', { maximumFractionDigits: 1 });

export interface AnnonsSlotRow {
  /** T.ex. "Budget 5 · 2 tagna · 1 begärd · 3 kvar". */
  text: string;
  /**
   * Det finns öppna annonsförfrågningar mot destinationen. Raden ska då visas i varningston:
   * de konkurrerar om samma slottar utan att synas i nettot.
   */
  hasRequested: boolean;
}

/**
 * Slottraden för ett annonsförslag. Delarna kommer ur backendens dagsbudget-uppdelning, där
 * `kvar = budget − tagna` medan BEGÄRDA medvetet ligger utanför nettot (platsen tas först vid
 * tilldelning). Just därför måste de begärda synas: utan dem kan två planeringskörningar lägga
 * förfrågningar mot samma slottar utan att någon upptäcker det.
 *
 * Nollposter utelämnas, så en oförbrukad destination blir kort ("Budget 5 · 5 kvar") och bara
 * det som faktiskt tar plats syns.
 */
export const formatAnnonsSlots = (
  annons: RouteAnnonsOptionDTO
): AnnonsSlotRow => {
  const parts = [`Budget ${formatSlotCount(annons.dayBudget)}`];

  if (annons.assignedToday > 0) {
    parts.push(
      `${annons.assignedToday} ${annons.assignedToday === 1 ? 'tagen' : 'tagna'}`
    );
  }
  if (annons.requestedToday > 0) {
    parts.push(
      `${annons.requestedToday} ${annons.requestedToday === 1 ? 'begärd' : 'begärda'}`
    );
  }
  parts.push(`${formatSlotCount(annons.remainingDayBudget)} kvar`);

  return { text: parts.join(' · '), hasRequested: annons.requestedToday > 0 };
};

/**
 * Varför luckan inte kunde få något annonsalternativ. Backend sätter ALLTID en orsak när
 * `assignmentRequest` är null, så raden kan visas avstängd med förklaringen i stället för att
 * försvinna tyst — annars går "annonsering är omöjlig här" inte att skilja från "budgeten tog
 * slut i dag", och en trafikledare som kunde ha höjt dagsbudgeten får aldrig veta det.
 *
 * Returnerar undefined när ett alternativ finns (då ska den riktiga raden visas) eller när
 * backend inte skickat någon orsak.
 */
/**
 * Varför persontransport inte gick att föreslå. Motsvarigheten till
 * {@link describeAnnonsUnavailable}: raden ska visas avstängd med sin orsak i stället för att
 * försvinna tyst, annars går "det går ingen buss" inte att skilja från "vi frågade aldrig".
 */
export const describePersonalTransportUnavailable = (
  gap: RouteGapSuggestionsDTO
): string | undefined => {
  if (gap.personalTransport) return undefined;
  return gap.personalTransportUnavailableReason == null
    ? 'ingen förbindelse föreslogs'
    : getPersonalTransportUnavailableLabel(
        gap.personalTransportUnavailableReason
      );
};

/**
 * Varför ingen utfyllnad kunde föreslås. Sökningen gäller ALLA oplanerade ben i
 * postnummerkorridoren, inte bara mellanlandade bilar — den gamla texten "ingen mellanlandad bil i
 * närheten" sa mer än sökningen gjorde. Backend skickar orsaken, så "korridoren är tom" går att
 * skilja från "allt i den är redan taget".
 */
export const describeFillerUnavailable = (
  gap: RouteGapSuggestionsDTO
): string | undefined => {
  if (gap.filler && gap.filler.availableCount > 0) return undefined;
  switch (gap.fillerUnavailableReason) {
    case RouteFillerUnavailableReasonEnum.NotSearched:
      return 'luckan saknar postnummer — ingen bil kunde sökas';
    case RouteFillerUnavailableReasonEnum.AllTaken:
      return 'uppdragen i korridoren är redan planerade, låsta eller väntar på förtransport';
    case RouteFillerUnavailableReasonEnum.NoneFitsTime:
      return 'inget uppdrag i korridoren hinns före nästa ben';
    case RouteFillerUnavailableReasonEnum.NoneInCorridor:
    default:
      return 'inget oplanerat uppdrag i korridoren';
  }
};

const describeConnectionIssue = (connection: RouteConnectionDTO): string => {
  switch (connection.issue) {
    case RouteConnectionIssueEnum.WaitTooLong:
      return `första avgången om ${connection.waitMinutes ?? '?'} min`;
    case RouteConnectionIssueEnum.TransitTooLong:
      return 'för lång resa';
    case RouteConnectionIssueEnum.ArrivesTooLate:
      return 'hinner inte fram';
    case RouteConnectionIssueEnum.BudgetExhausted:
      return 'dagens sökbudget var slut';
    case RouteConnectionIssueEnum.LookupFailed:
      return 'sökningen misslyckades';
    case RouteConnectionIssueEnum.DepartsDayBefore:
      return 'resan går dagen innan';
    default:
      return 'ingen kollektivtrafik';
  }
};

/**
 * En anslutning till eller från samåkningsbilen som text. En promenad visas med sin tid — den
 * bokas inte, men tar tid och styr när nästa steg kan börja. `where` är t.ex. "till bilen i
 * Uppsala". Undefined när det inte finns något att säga.
 */
export const describeConnection = (
  connection: RouteConnectionDTO | undefined,
  where: string
): string | undefined => {
  if (!connection) return undefined;
  const km = `${Math.round(connection.km)} km`;
  switch (connection.mode) {
    case RouteConnectionModeEnum.Transit: {
      const t = connection.transit!;
      const wait = connection.waitMinutes ? `, väntar ${connection.waitMinutes} min` : '';
      return `kollektivt ${where} ${formatClock(new Date(t.departureTimeEpoch * 1000))}–${formatClock(new Date(t.arrivalTimeEpoch * 1000))} (${km}${wait})`;
    }
    case RouteConnectionModeEnum.Unresolved:
      return `${km} ${where}: ${describeConnectionIssue(connection)}`;
    case RouteConnectionModeEnum.Walk:
      return connection.estimatedMinutes
        ? `promenad ${where} (${connection.estimatedMinutes} min)`
        : undefined;
    default:
      return undefined;
  }
};

/**
 * Samåkningens anslutning som text, eller undefined när det inte finns något att visa (t.ex. en
 * promenad på 0 km när bilen står vid avlämningen). Börjar anslutningen efter att bilen är framme
 * är det resan vidare från avstigningen; annars är det resan till bilen.
 */
export const describeRideAlongConnection = (
  ride: RouteRideAlongOptionDTO
): string | undefined => {
  const { connection } = ride;
  if (!connection?.departureTime || connection.km === 0) return undefined;
  return describeConnection(
    connection,
    new Date(connection.departureTime).getTime() >= new Date(ride.arrivalTime).getTime()
      ? `från ${ride.toCity}`
      : `till bilen i ${ride.fromCity}`
  );
};

export const describeAnnonsUnavailable = (
  gap: RouteGapSuggestionsDTO
): string | undefined => {
  const reason = gap.assignmentRequestUnavailableReason;
  if (gap.assignmentRequest || reason == null) return undefined;

  switch (reason) {
    case RouteAnnonsUnavailableReasonEnum.MissingCoordinates:
      return 'luckans ändpunkter saknar koordinater';
    case RouteAnnonsUnavailableReasonEnum.NoLogisticsCenterNearStart:
      return 'inget logistikcenter inom räckhåll från startpunkten';
    case RouteAnnonsUnavailableReasonEnum.NoFacilityNearEnd:
      return 'ingen annonsanläggning inom räckhåll från luckans slut';
    case RouteAnnonsUnavailableReasonEnum.BudgetExhausted: {
      const namn = gap.nearestFacilityName || 'Närmaste anläggning';
      const kvar = gap.nearestFacilityRemainingBudget;
      // Negativt = fler bilar redan inne än budgeten tillåter. Att skriva "−8 kvar" läser fel;
      // överbokningen är det TL behöver veta för att bedöma om en höjning är rimlig.
      if (kvar != null && kvar < 0) {
        const antal = Math.abs(kvar);
        return `${namn} är överbokad med ${formatSlotCount(antal)} ${antal === 1 ? 'bil' : 'bilar'} i dag`;
      }
      return `${namn} har ingen dagsbudget kvar i dag`;
    }
    case RouteAnnonsUnavailableReasonEnum.NoWayToLogisticsCenter:
      return 'ingen väg till logistikcentret — varken bil dit eller kollektivtrafik som hinns';
    case RouteAnnonsUnavailableReasonEnum.DoesNotFitDay:
      return 'ryms inte i dagen med resan till logistikcentret och vidare till luckans slut';
    case RouteAnnonsUnavailableReasonEnum.FacilityTooFarFromEnd: {
      const namn = gap.nearestFacilityName || 'Anläggningen med budget';
      return `${namn} ligger för långt från luckans slut — resten vore en ny lucka`;
    }
    case RouteAnnonsUnavailableReasonEnum.ApproachNotChecked:
      return 'resan till logistikcentret hann inte prövas — körningens sökningar efter tillresor tog slut';
    case RouteAnnonsUnavailableReasonEnum.FacilityLocked: {
      const namn = gap.nearestFacilityName || 'Närmaste anläggning';
      return `${namn} är låst för annonsförfrågningar till midnatt`;
    }
    default:
      return undefined;
  }
};

/**
 * Hur fälttestaren kommer till logistikcentret där annonsbilen hämtas, och vidare från
 * anläggningen — för detaljraden under en vald annons. Utan raden såg annonsen ut som en
 * enstegslösning, vilket var precis felet i betygsfeedbacken paket #49.
 */
export const describeAnnonsApproach = (
  annons: RouteAnnonsOptionDTO
): string => {
  const via = (() => {
    switch (annons.approachKind) {
      case RouteAnnonsApproachKindEnum.Filler: {
        const f = annons.approachFiller;
        const start = annons.approachFillerPlannedStart
          ? ` från ${formatClock(annons.approachFillerPlannedStart)}`
          : '';
        return f
          ? `Tillresa med ${f.topCandidateIsStopover ? 'mellanlandad bil' : 'oplanerat uppdrag'} ${f.topCandidateRegistrationNumber ?? ''} ${f.topCandidateFromCity ?? ''} → ${f.topCandidateToCity ?? ''}${start}`.replace(
              /\s+/g,
              ' '
            )
          : 'Tillresa med bil';
      }
      case RouteAnnonsApproachKindEnum.PersonalTransport: {
        const t = annons.approachTransit;
        return t
          ? `Tillresa kollektivt ${formatClock(new Date(t.departureTimeEpoch * 1000))} → ${formatClock(new Date(t.arrivalTimeEpoch * 1000))} (${Math.round(t.durationSeconds / 60)} min)`
          : 'Tillresa kollektivt';
      }
      default:
        return 'Står vid logistikcentret';
    }
  })();

  return `${via} · sedan ${Math.round(annons.connectionKm)} km vidare, framme ${formatClock(annons.arrivalTime)}`;
};

/** En rad i paketlistan: ett ensamt paket, eller ett skiftespar som hålls samman. */
export type PackageRow =
  | { kind: 'single'; pkg: RoutePackageDTO }
  | { kind: 'swap'; swap: RouteDriverSwapDTO; packages: RoutePackageDTO[] };

/**
 * Grupperar paketlistan så att ett skiftespar blir EN rad med två kort i.
 *
 * Paren måste hållas samman i vyn eftersom de verkställs tillsammans — ett halvt verkställt skifte
 * lämnar en förare vid bytespunkten utan bil hem. Sorteringen bestäms av den först förekommande
 * halvan, så listans ordning i övrigt inte kastas om.
 *
 * Saknas motparten (den kan ha filtrerats bort) renderas halvan som ett ensamt paket — hellre ett
 * paket utan ram än ett paket som försvinner.
 */
export const groupPackagesForDisplay = (
  packages: RoutePackageDTO[],
  swapByPackageIndex: Record<number, RouteDriverSwapDTO>
): PackageRow[] => {
  const rows: PackageRow[] = [];
  const consumed = new Set<number>();

  packages.forEach((pkg) => {
    if (consumed.has(pkg.packageIndex)) return;

    const swap = swapByPackageIndex[pkg.packageIndex];
    if (!swap) {
      rows.push({ kind: 'single', pkg });
      return;
    }

    const partnerIndex =
      swap.sideA.packageIndex === pkg.packageIndex
        ? swap.sideB.packageIndex
        : swap.sideA.packageIndex;
    const partner = packages.find((p) => p.packageIndex === partnerIndex);

    if (!partner) {
      rows.push({ kind: 'single', pkg });
      return;
    }

    consumed.add(pkg.packageIndex);
    consumed.add(partner.packageIndex);
    rows.push({ kind: 'swap', swap, packages: [pkg, partner] });
  });

  return rows;
};

// ── Klassningen: två listor ────────────────────────────────────────────────────

export interface PlanningClassPartition {
  auto: RoutePackageDTO[];
  manual: RoutePackageDTO[];
}

/**
 * Delar förslaget i fliken "Redo att tillämpa" och fliken "Behöver input". Ordningen inom varje
 * lista bevaras — den bär planeringens egen prioritet (tidigast start först), och en omsortering
 * här hade gjort listan svårare att jämföra med gårdagens körning.
 *
 * Ett SKIFTESPAR följs åt. Paret verkställs tillsammans — ett halvt verkställt skifte lämnar en
 * förare vid bytespunkten utan bil hem — så är ena halvan manuell är båda det. Utan den regeln
 * hamnade halvorna i var sin flik: den automatiska renderades som ett ensamt paket med ikryssad
 * ruta, medan buildPackageActions tyst gav noll actions eftersom motparten var omarkerad, och
 * ingenting i vyn förklarade varför paketet aldrig verkställdes.
 */
export const partitionByPlanningClass = (
  packages: RoutePackageDTO[],
  swapByPackageIndex: Record<number, RouteDriverSwapDTO> = {}
): PlanningClassPartition => {
  const isManual = (pkg: RoutePackageDTO): boolean => {
    if (pkg.planningClass === RoutePlanPlanningClassEnum.Manual) return true;

    const swap = swapByPackageIndex[pkg.packageIndex];
    if (!swap) return false;

    const partnerIndex =
      swap.sideA.packageIndex === pkg.packageIndex
        ? swap.sideB.packageIndex
        : swap.sideA.packageIndex;
    const partner = packages.find((p) => p.packageIndex === partnerIndex);
    return partner?.planningClass === RoutePlanPlanningClassEnum.Manual;
  };

  return {
    auto: packages.filter((p) => !isManual(p)),
    manual: packages.filter(isManual),
  };
};

// ── Körningsspåret ────────────────────────────────────────────────────────────

/**
 * Raderna i körningsspåret som gäller ett visst paket, i körordning.
 *
 * Ordningen kommer ur stegen och inte ur listpositionen: spåret skrivs medan körningen pågår, men
 * hemresepasset går tillbaka och skriver på paket som redan passerat, så en rå läsning av listan
 * kan visa klassningen före seeden. Rader utan paketindex hör till stegraden och hoppas över.
 */
export const traceForPackage = (
  trace: RoutePlanTraceEntryDTO[],
  packageIndex: number
): RoutePlanTraceEntryDTO[] =>
  trace
    .map((entry, order) => ({ entry, order }))
    .filter(({ entry }) => entry.packageIndex === packageIndex)
    .sort((a, b) => a.entry.step - b.entry.step || a.order - b.order)
    .map(({ entry }) => entry);
